import { useEffect } from 'react'
import deepmerge from 'deepmerge'
import cloneDeep from 'lodash/cloneDeep'

import { usePreviousValue, useSetState } from '@campaignhub/react-hooks'
import { deepSetObject, digObject, generateID } from '@campaignhub/javascript-utils'

const mergeLayoutOptions = (defaultLayout, customizedLayout) => {
  const defaultOptions = defaultLayout.options || {}
  const customOptions = customizedLayout.options || {}

  const mergedOptions = deepmerge(defaultOptions, customOptions)

  return mergedOptions
}

const getLayout = (defaultLayout, customizedLayout) => {
  if (customizedLayout && customizedLayout.columns){
    return {
      ...customizedLayout,
      options: mergeLayoutOptions(defaultLayout, customizedLayout),
    }
  }

  return defaultLayout || {}
}

const reorderColumn = (column, sourceIndex, destinationIndex) => {
  const result = Array.from(column)
  const [removed] = result.splice(sourceIndex, 1)
  result.splice(destinationIndex, 0, removed)

  return result
}

const moveBetweenColumns = (data = {}) => {
  const {
    sourceIndex,
    destinationIndex,
    destinationColumnModules,
    destinationDroppableId,
    sourceColumnModules,
    sourceDroppableId,
  } = data

  // Remove from source column
  const [removed] = sourceColumnModules.splice(sourceIndex, 1)

  // Add to new column
  destinationColumnModules.splice(destinationIndex, 0, removed)

  return {
    [destinationDroppableId]: destinationColumnModules,
    [sourceDroppableId]: sourceColumnModules,
  }
}

const updateColumns = (data = {}) => {
  const {
    sourceIndex,
    destinationIndex,
    destinationColumnModules,
    destinationDroppableId,
    sourceDroppableId,
  } = data

  // Just reordering a column
  if (sourceDroppableId === destinationDroppableId){
    const updatedSort = reorderColumn(destinationColumnModules, sourceIndex, destinationIndex)
    return {
      [destinationDroppableId]: updatedSort,
    }
  }

  // Moving a module between columns
  return moveBetweenColumns(data)
}

const updateLayout = (dragResult, state, setState) => {
  const {
    source: { index: sourceIndex, droppableId: sourceDroppableId },
    destination: { index: destinationIndex, droppableId: destinationDroppableId },
  } = dragResult

  const { activeLayout } = state

  // Create a new object in memory
  const layoutClone = cloneDeep(activeLayout)

  // Safety check that new have columns with these IDs
  // Return if we dont
  const sourceColumn = layoutClone.columns[sourceDroppableId]
  const destinationColumn = layoutClone.columns[destinationDroppableId]
  if (!sourceColumn || !destinationColumn){
    return
  }

  const sourceColumnModules = sourceColumn.modules || []
  const destinationColumnModules = destinationColumn.modules || []

  // Update the columns - either sort or move item between them
  const updatedColumns = updateColumns({
    sourceIndex,
    destinationIndex,
    destinationColumnModules,
    destinationDroppableId,
    sourceColumnModules,
    sourceDroppableId,
  })

  // Map updated columns to match existing format with ID and modules object
  const mappedColumns = Object.keys(updatedColumns).reduce((column, key) => {
    // eslint-disable-next-line no-param-reassign
    column[key] = {
      ...layoutClone.columns[key],
      modules: updatedColumns[key],
    }

    return column
  }, {})

  const updatedLayout = {
    ...layoutClone,
    custom: true,
    columns: {
      ...layoutClone.columns,
      ...mappedColumns,
    },
  }

  setState({
    activeLayout: updatedLayout,
  })
}

const onDragEnd = (dragResult, state, setState) => {
  const { source, destination } = dragResult

  // Dropped outside of droppable zones or in the same spot
  if (
    !destination
    || (source.index === destination.index
      && source.droppableId === destination.droppableId)
  ){
    return
  }

  updateLayout(dragResult, state, setState)
}

const modifyModuleOptions = (payload, state) => {
  const {
    moduleKey, optionKey, paramKey, value,
  } = payload

  const { activeLayout } = state
  if (!activeLayout) return null

  const cloneLayout = cloneDeep(activeLayout)

  const updatedLayout = deepSetObject(cloneLayout, `options.${moduleKey}.${optionKey}.${paramKey}`, value)

  return updatedLayout
}

const updateModuleOptions = (payload, state, setState) => {
  setState({ activeLayout: modifyModuleOptions(payload, state) })
}

const cloneColumnModules = (activeLayout) => {
  if (!activeLayout) return null

  const updatedLayout = cloneDeep(activeLayout)

  return updatedLayout
}

export const setupDefaultOptions = (configOptions, options = {}) => {
  if (!configOptions) return null

  const defaultOptions = {}

  Object.values(configOptions).forEach((option) => {
    const { key, optionKey, values } = option

    // Add the option key e.g. "customization" or "queryParams"
    if (!defaultOptions[optionKey]) defaultOptions[optionKey] = {}

    // Set the value for the key e.g. display_mode: "tiles" or title: "Campaigns"
    // We can pass through a value using the options above or use the key of the
    // first value from the configOption
    let value = options[key]
    if (values && values[0]){
      value = values[0].key
    }

    // Only set the value if one is present
    if (value){
      defaultOptions[optionKey][key] = value
    }
  })

  return defaultOptions
}

const addModule = (payload, props, state, setState) => {
  const { moduleId, parentId } = payload
  const { availableModules } = props
  const { activeLayout } = state

  // Add module to layout
  const moduleTemplate = availableModules[moduleId]
  if (!moduleTemplate) return

  const updatedLayout = cloneColumnModules(activeLayout)
  if (!updatedLayout) return

  const moduleKey = `${moduleTemplate.id}-${generateID()}`
  const newModule = {
    key: moduleKey,
    moduleId,
  }

  updatedLayout.columns[parentId].modules.push(newModule)

  // Set default options
  const defaultOptions = setupDefaultOptions(moduleTemplate.configOptions, { title: moduleTemplate.title })
  if (defaultOptions) updatedLayout.options[moduleKey] = defaultOptions

  setState({ activeLayout: updatedLayout, showAddDashboardModuleModal: false })
}

const deleteModule = (payload, state, setState) => {
  const { moduleKey, parentId } = payload
  const { activeLayout } = state

  const updatedLayout = cloneColumnModules(activeLayout)
  if (!updatedLayout) return

  // Remove the module
  const index = updatedLayout.columns[parentId].modules.findIndex(m => m.key === moduleKey)
  updatedLayout.columns[parentId].modules.splice(index, 1)

  // Remove config
  if (updatedLayout.options[moduleKey]){
    delete updatedLayout.options[moduleKey]
  }

  setState({ activeLayout: updatedLayout })
}

const defaultState = {
  activeLayout: null,
  loadedModules: [],
  showAddDashboardModuleModal: false,
}

function useCustomizableDashboard(options = {}){
  const {
    availableModules,
    columns,
    customizedLayout,
    callbacks: { saveDashboard },
    dashboardKey,
    dashboardReady,
    defaultLayout,
    editing,
  } = options || {}

  const prevEditingValue = usePreviousValue(editing)

  const layout = getLayout(defaultLayout, customizedLayout)

  const [state, setState] = useSetState(defaultState)
  const { activeLayout, loadedModules, showAddDashboardModuleModal } = state

  useEffect(() => {
    // Don't set layout until we have loaded the user
    // to prevent rendering default then custom
    if (dashboardReady){
      setState({ activeLayout: layout })
    }
  }, [dashboardReady, layout.custom])

  useEffect(() => {
    // Toggled from editing = true to editing = false
    if (prevEditingValue && saveDashboard && !editing){
      saveDashboard(dashboardKey, activeLayout)
    }
  }, [editing, prevEditingValue])

  return {
    activeLayout,
    availableModules,
    callbacks: {
      addModule: payload => addModule(payload, options, state, setState),
      deleteModule: payload => deleteModule(payload, state, setState),
      onDragEnd: dragResult => onDragEnd(dragResult, state, setState),
      moduleLoadComplete: payload => setState({ loadedModules: [...loadedModules, payload] }),
      updateModuleOptions: payload => updateModuleOptions(payload, state, setState),
    },
    columns,
    editing,
    loadedModules,
    moduleOptions: digObject(activeLayout, 'options', {}),
    setState,
    showAddDashboardModuleModal,
    state,
  }
}

export default useCustomizableDashboard